import React, { useState, useEffect } from 'react';
import { BasicPage } from '../components/BasicPage';
import { useAuth, apiFetch } from '../hooks/useAuth';
import firebaseConfig from '../config/firebaseConfig';
import {
    Box, Typography, Card, CardContent, Grid, Alert, AlertTitle, LinearProgress,
    Button, Paper, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Storage as StorageIcon,
    Refresh as RefreshIcon,
    TableChart as DatabaseIcon,
    CloudDownload as CloudDownloadIcon,
    FileDownload as FileDownloadIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';

// Configure dayjs for UTC handling
dayjs.extend(utc);
dayjs.extend(timezone);

export const DataImport = () => {
    const { accountID, claims } = useAuth();
    const [tableStats, setTableStats] = useState(null);
    const [insertActivity, setInsertActivity] = useState(null);
    const [pdfEncryptionStats, setPdfEncryptionStats] = useState(null);
    const [pendingFiles, setPendingFiles] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastRefresh, setLastRefresh] = useState(null);
    const [pdfEncryptionLoading, setPdfEncryptionLoading] = useState(false);
    const [pdfEncryptionResult, setPdfEncryptionResult] = useState(null);

    // Helper function to parse MySQL timestamps correctly
    // MySQL returns timestamps that need 1 hour adjustment for Germany
    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;
        return dayjs(timestamp).subtract(1, 'hour');
    };
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [missingInvoices, setMissingInvoices] = useState(null);
    const [missingInvoicesLoading, setMissingInvoicesLoading] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState('all'); // Filter state for document types
    const [cleaningNonPdfFiles, setCleaningNonPdfFiles] = useState(false);
    const [cleanNonPdfResult, setCleanNonPdfResult] = useState(null);

    // CSV Export function
    const exportToCSV = () => {
        if (!missingInvoices?.missingFiles) return;
        
        const filteredData = missingInvoices.missingFiles.filter(invoice => 
            selectedDocumentType === 'all' || 
            invoice.documentType === selectedDocumentType
        );

        // Create CSV content
        const headers = ['Belegnummer', 'Kundennummer', 'Datum', 'Belegart', 'Betrag', 'Erwarteter Dateiname'];
        const csvContent = [
            headers.join(';'),
            ...filteredData.map(invoice => [
                invoice.invoiceNumber,
                invoice.customerNumber,
                invoice.invoiceDate ? dayjs(invoice.invoiceDate).format('DD.MM.YYYY') : '-',
                invoice.documentType,
                (invoice.totalAmount || 0).toFixed(2).replace('.', ','),
                invoice.expectedFilename1
            ].join(';'))
        ].join('\n');

        // Create and download file
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `fehlende_dateien_${selectedDocumentType}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Check if user is Super Admin for Schöningh
    const isSuperAdmin = claims?.role === "SuperAdmin";
    const isSchoeningh = firebaseConfig.projectId === 'litport-schoeningh';
    
    console.log("DataImport Debug:", {
        isSuperAdmin,
        isSchoeningh,
        superuser: claims?.superuser,
        role: claims?.role,
        claims: claims
    });

    useEffect(() => {
        if (isSuperAdmin && isSchoeningh) {
            fetchTableStats();
            fetchInsertActivity();
            fetchPdfEncryptionStats();
            fetchPendingFiles();
            fetchMissingInvoices();
        } else {
            setLoading(false);
        }
    }, [accountID, isSuperAdmin, isSchoeningh]);

    const fetchTableStats = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await apiFetch('/app/data-import/table-stats');
            const data = await response.json();
            
            if (response.ok) {
                setTableStats(data);
                setLastRefresh(new Date());
            } else {
                setError('Fehler beim Laden der Tabellen-Statistiken');
            }
        } catch (err) {
            setError(err.message || 'Fehler beim Laden der Tabellen-Statistiken');
        } finally {
            setLoading(false);
        }
    };


    const fetchInsertActivity = async () => {
        try {
            const response = await apiFetch('/app/data-import/insert-activity');
            const data = await response.json();
            
            if (response.ok) {
                setInsertActivity(data);
            } else {
                console.error('Error fetching insert activity:', data);
            }
        } catch (err) {
            console.error('Error fetching insert activity:', err);
        }
    };

    const fetchPdfEncryptionStats = async () => {
        try {
            const response = await apiFetch('/app/data-import/pdf-encryption-stats');
            const data = await response.json();
            
            if (response.ok) {
                setPdfEncryptionStats(data);
            } else {
                console.error('Error fetching PDF encryption stats:', data);
            }
        } catch (err) {
            console.error('Error fetching PDF encryption stats:', err);
        }
    };

    const fetchPendingFiles = async () => {
        try {
            const response = await apiFetch('/app/data-import/pending-encryption-files');
            const data = await response.json();
            
            if (response.ok) {
                setPendingFiles(data);
            } else {
                console.error('Error fetching pending files:', data);
                // Set empty state to stop loading
                setPendingFiles({ pdfFiles: 0, nonPdfFiles: 0, allFiles: [] });
            }
        } catch (err) {
            console.error('Error fetching pending files:', err);
            // Set empty state to stop loading
            setPendingFiles({ pdfFiles: 0, nonPdfFiles: 0, allFiles: [] });
        }
    };

    const fetchMissingInvoices = async () => {
        try {
            setMissingInvoicesLoading(true);
            setError('');
            console.log('Fetching missing invoices...');
            const response = await apiFetch('/app/data-import/missing-invoice-files');
            const data = await response.json();
            
            console.log('Missing invoices response:', data);
            
            if (response.ok) {
                console.log('Setting missing invoices:', data);
                setMissingInvoices(data);
            } else {
                console.error('Error fetching missing invoices:', data);
                setError(data.message || 'Fehler beim Laden der fehlenden Dateien');
            }
        } catch (err) {
            console.error('Error fetching missing invoices:', err);
            setError(err.message || 'Fehler beim Laden der fehlenden Dateien');
        } finally {
            setMissingInvoicesLoading(false);
        }
    };

    const handleCleanNonPdfFiles = async () => {
        if (!window.confirm('Möchten Sie wirklich alle Nicht-PDF-Dateien aus dem uploads/ Ordner löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
            return;
        }

        try {
            setCleaningNonPdfFiles(true);
            setCleanNonPdfResult(null);

            const response = await apiFetch('/app/data-import/clean-non-pdf-files', {
                method: 'DELETE'
            });
            const data = await response.json();

            if (response.ok && data.success) {
                setCleanNonPdfResult({
                    success: true,
                    message: data.message,
                    deletedCount: data.deletedCount,
                    deletedFiles: data.deletedFiles,
                    errors: data.errors
                });
                // Refresh pending files list
                fetchPendingFiles();
            } else {
                setCleanNonPdfResult({
                    success: false,
                    message: data.message || 'Fehler beim Löschen der Dateien'
                });
            }
        } catch (err) {
            console.error('Error cleaning non-PDF files:', err);
            setCleanNonPdfResult({
                success: false,
                message: 'Netzwerkfehler beim Löschen der Dateien'
            });
        } finally {
            setCleaningNonPdfFiles(false);
        }
    };

    const triggerPdfEncryption = async () => {
        try {
            setPdfEncryptionLoading(true);
            setPdfEncryptionResult(null);
            
            const response = await apiFetch('/app/data-import/trigger-pdf-encryption', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setPdfEncryptionResult(data);
                // Refresh PDF encryption stats and pending files after successful trigger
                fetchPdfEncryptionStats();
                fetchPendingFiles();
            } else {
                setPdfEncryptionResult({
                    success: false,
                    message: data.message || 'Error triggering PDF encryption',
                    error: data.error
                });
            }
        } catch (err) {
            console.error('Error triggering PDF encryption:', err);
            setPdfEncryptionResult({
                success: false,
                message: 'Error triggering PDF encryption',
                error: err.message
            });
        } finally {
            setPdfEncryptionLoading(false);
        }
    };


    const syncEncryptedFiles = async () => {
        try {
            setSyncLoading(true);
            setSyncResult(null);
            setSyncProgress({ current: 0, total: 0, percentage: 0 });
            
            // First, get list of directories to sync
            const dirsResponse = await apiFetch('/app/data-import/sync-directories');
            const dirsData = await dirsResponse.json();
            
            if (!dirsResponse.ok) {
                throw new Error(dirsData.message || 'Error getting directories');
            }
            
            const directories = dirsData.directories || [];
            const totalFiles = dirsData.totalFiles || 0;
            
            console.log(`Found ${directories.length} directories with ${totalFiles} total files`);
            
            let processedFiles = 0;
            let totalInserted = 0;
            let totalErrors = 0;
            
            // Process each directory
            for (let i = 0; i < directories.length; i++) {
                const dir = directories[i];
                
                console.log(`Processing directory ${i + 1}/${directories.length}: ${dir.name} (${dir.fileCount} files)`);
                
                const response = await apiFetch('/app/data-import/sync-encrypted-files', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ directory: dir.path })
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                    console.error(`Error syncing ${dir.name}:`, data.message);
                    totalErrors++;
                } else {
                    totalInserted += data.results.inserted || 0;
                    processedFiles += data.filesProcessed || 0;
                }
                
                // Update progress
                setSyncProgress({
                    current: processedFiles,
                    total: totalFiles,
                    percentage: Math.round((processedFiles / totalFiles) * 100),
                    currentDirectory: dir.name,
                    directoriesProcessed: i + 1,
                    totalDirectories: directories.length
                });
                
                // Small delay between directories
                if (i < directories.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            
            setSyncResult({
                success: true,
                message: 'Sync completed successfully',
                totalInserted,
                totalErrors,
                directoriesProcessed: directories.length,
                totalFiles: processedFiles,
                alreadyExisted: processedFiles - totalInserted
            });
            
        } catch (err) {
            console.error('Error syncing encrypted files:', err);
            setSyncResult({
                success: false,
                message: 'Error syncing encrypted files',
                error: err.message
            });
        } finally {
            setSyncLoading(false);
        }
    };


    // Access control with detailed debugging
    console.log("DataImport Access Check:", {
        isSuperAdmin,
        isSchoeningh,
        claims,
        superuser: claims?.superuser,
        role: claims?.role,
        projectId: firebaseConfig.projectId
    });

    if (!isSuperAdmin || !isSchoeningh) {
        return (
            <BasicPage title="Import Status" icon={<StorageIcon />}>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">
                        Zugriff verweigert. Diese Seite ist nur für Super Admins verfügbar.
                    </Alert>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100' }}>
                        <Typography variant="body2" component="pre">
                            Debug Info: {JSON.stringify({
                                isSuperAdmin,
                                isSchoeningh,
                                claims,
                                projectId: firebaseConfig.projectId
                            }, null, 2)}
                        </Typography>
                    </Box>
                </Box>
            </BasicPage>
        );
    }

    if (loading) {
        return (
            <BasicPage title="Import Status" icon={<StorageIcon />}>
                <Box sx={{ width: '100%', mt: 4 }}>
                    <LinearProgress />
                    <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                        Lade Tabellen-Statistiken...
                    </Typography>
                </Box>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Import Status" icon={<StorageIcon />}>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Datenbank-Statistiken
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Aktuelle Anzahl der Datensätze in allen Tabellen
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={loading ? <LinearProgress size={16} /> : <RefreshIcon />}
                                    onClick={() => {
                                        fetchTableStats();
                                        fetchInsertActivity();
                                        fetchPdfEncryptionStats();
                                        fetchPendingFiles();
                                        fetchMissingInvoices();
                                    }}
                                    disabled={loading}
                                    sx={{ borderColor: '#3caf82', color: '#3caf82', '&:hover': { borderColor: '#2d8a62', bgcolor: 'rgba(60, 175, 130, 0.04)' } }}
                                >
                                    {loading ? 'Lade...' : 'Aktualisieren'}
                                </Button>
                                {lastRefresh && (
                                    <Typography variant="caption" color="text.secondary">
                                        Letzte Aktualisierung: {dayjs(lastRefresh).format('DD.MM.YYYY HH:mm:ss')}
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Insert Activity Section (NEW - Trigger-based tracking) */}
                {insertActivity && (
                    <Box sx={{ mb: 3 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    <DatabaseIcon sx={{ mr: 1, alignSelf: 'center' }} />
                                    Echtzeit INSERT-Aktivität (letzte 30 Tage)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Automatisch durch Trigger erfasste INSERT-Operationen pro Tabelle
                                </Typography>
                                
                                {/* Summary Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {insertActivity.totals.totalInserts.toLocaleString('de-DE')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Gesamt INSERTs
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {insertActivity.totals.trackedTables}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Überwachte Tabellen
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {insertActivity.totals.activeDays}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Aktive Tage
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {insertActivity.totals.activeDays > 0 
                                                    ? Math.round(insertActivity.totals.totalInserts / insertActivity.totals.activeDays).toLocaleString('de-DE')
                                                    : 0}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Ø INSERTs/Tag
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Table Summary */}
                                {insertActivity.summary && insertActivity.summary.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Zusammenfassung pro Tabelle
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><strong>Tabelle</strong></TableCell>
                                                        <TableCell align="right"><strong>Gesamt INSERTs</strong></TableCell>
                                                        <TableCell align="right"><strong>Aktive Tage</strong></TableCell>
                                                        <TableCell><strong>Erste Aktivität</strong></TableCell>
                                                        <TableCell><strong>Letzte Aktivität</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {insertActivity.summary.map((row, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                                    {row.table_name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <strong>{row.total_inserts.toLocaleString('de-DE')}</strong>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {row.active_days}
                                                            </TableCell>
                                                            <TableCell>
                                                                {parseTimestamp(row.first_activity)?.format('DD.MM.YYYY HH:mm') || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {parseTimestamp(row.last_activity)?.format('DD.MM.YYYY HH:mm') || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {/* Daily Activity Breakdown */}
                                {insertActivity.dailyActivity && insertActivity.dailyActivity.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Tägliche Aktivität (Details)
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><strong>Datum</strong></TableCell>
                                                        <TableCell><strong>Tabelle</strong></TableCell>
                                                        <TableCell align="right"><strong>INSERTs</strong></TableCell>
                                                        <TableCell align="right"><strong>Aktive Stunden</strong></TableCell>
                                                        <TableCell><strong>Erste/Letzte Aktivität</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {insertActivity.dailyActivity.map((row, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                {dayjs(row.activity_date).format('DD.MM.YYYY')}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                                    {row.table_name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Chip 
                                                                    label={row.total_inserts.toLocaleString('de-DE')} 
                                                                    color="primary" 
                                                                    size="small"
                                                                    sx={{ bgcolor: '#3caf82', color: 'white' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {row.active_hours}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption">
                                                                    {parseTimestamp(row.first_insert)?.format('HH:mm') || '-'} - {parseTimestamp(row.last_insert)?.format('HH:mm') || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* PDF Encryption Statistics Section */}
                {pdfEncryptionStats && (
                    <Box sx={{ mb: 3 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    <CloudDownloadIcon sx={{ mr: 1, alignSelf: 'center' }} />
                                    PDF-Verschlüsselung Statistiken
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Automatische PDF-Verschlüsselung aus dem uploads/ Verzeichnis
                                </Typography>
                                
                                {/* Manual Trigger Buttons */}
                                <Box sx={{ mb: 3 }}>
                                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<CloudDownloadIcon />}
                                            onClick={triggerPdfEncryption}
                                            disabled={pdfEncryptionLoading || syncLoading}
                                            sx={{ borderColor: '#3caf82', color: '#3caf82', '&:hover': { borderColor: '#2d8a62', bgcolor: 'rgba(60, 175, 130, 0.04)' } }}
                                        >
                                            {pdfEncryptionLoading ? 'PDF-Verschlüsselung läuft...' : 'PDF-Verschlüsselung manuell starten'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<DatabaseIcon />}
                                            onClick={syncEncryptedFiles}
                                            disabled={syncLoading || pdfEncryptionLoading}
                                            sx={{ borderColor: '#3caf82', color: '#3caf82', '&:hover': { borderColor: '#2d8a62', bgcolor: 'rgba(60, 175, 130, 0.04)' } }}
                                        >
                                            {syncLoading ? 'Sync läuft...' : 'Verschlüsselte Dateien in DB synchronisieren'}
                                        </Button>
                                    </Stack>
                                    
                                    {/* Show PDF encryption result */}
                                    {pdfEncryptionResult && (
                                        <Box sx={{ mt: 2 }}>
                                            {pdfEncryptionResult.success ? (
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    <AlertTitle>PDF-Verschlüsselung erfolgreich</AlertTitle>
                                                    {pdfEncryptionResult.message}
                                                    <br />
                                                    <strong>Verarbeitet:</strong> {pdfEncryptionResult.results?.encrypted || 0} von {pdfEncryptionResult.results?.scanned || 0} Dateien
                                                    <br />
                                                    <strong>Dauer:</strong> {pdfEncryptionResult.duration || 0} Sekunden
                                                </Alert>
                                            ) : (
                                                <Alert severity="error" sx={{ mb: 2 }}>
                                                    <AlertTitle>Fehler bei PDF-Verschlüsselung</AlertTitle>
                                                    {pdfEncryptionResult.message}
                                                    {pdfEncryptionResult.error && (
                                                        <><br /><strong>Details:</strong> {pdfEncryptionResult.error}</>
                                                    )}
                                                </Alert>
                                            )}
                                        </Box>
                                    )}
                                    
                                    {/* Show sync progress */}
                                    {syncLoading && syncProgress && (
                                        <Box sx={{ mt: 2 }}>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                <AlertTitle>Synchronisierung läuft...</AlertTitle>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    Fortschritt: {syncProgress.current} von {syncProgress.total} Dateien ({syncProgress.percentage}%)
                                                </Typography>
                                                {syncProgress.currentDirectory && (
                                                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                                        Aktuelles Verzeichnis: {syncProgress.currentDirectory} ({syncProgress.directoriesProcessed}/{syncProgress.totalDirectories})
                                                    </Typography>
                                                )}
                                                <LinearProgress variant="determinate" value={syncProgress.percentage} />
                                            </Alert>
                                        </Box>
                                    )}
                                    
                                    {/* Show sync result */}
                                    {syncResult && (
                                        <Box sx={{ mt: 2 }}>
                                            {syncResult.success ? (
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    <AlertTitle>Synchronisierung erfolgreich</AlertTitle>
                                                    {syncResult.message}
                                                    <br />
                                                    <strong>Gesamte Dateien:</strong> {syncResult.totalFiles || 0}
                                                    <br />
                                                    <strong>Neu eingefügt/aktualisiert:</strong> {syncResult.totalInserted || 0}
                                                    <br />
                                                    <strong>Bereits vorhanden:</strong> {syncResult.alreadyExisted || 0}
                                                    {syncResult.directoriesProcessed && (
                                                        <><br /><strong>Verzeichnisse:</strong> {syncResult.directoriesProcessed}</>
                                                    )}
                                                    {syncResult.totalErrors > 0 && (
                                                        <><br /><strong>Fehler:</strong> {syncResult.totalErrors}</>
                                                    )}
                                                </Alert>
                                            ) : (
                                                <Alert severity="error" sx={{ mb: 2 }}>
                                                    <AlertTitle>Fehler bei Synchronisierung</AlertTitle>
                                                    {syncResult.message}
                                                    {syncResult.error && (
                                                        <><br /><strong>Details:</strong> {syncResult.error}</>
                                                    )}
                                                </Alert>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                
                                {/* Summary Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#3caf82', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {pdfEncryptionStats.summary.totalEncrypted.toLocaleString('de-DE')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Verschlüsselte PDFs
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {pdfEncryptionStats.summary.totalRuns}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Gesamtläufe
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {pdfEncryptionStats.summary.successRate}%
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Erfolgsrate
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#D84A48', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {pdfEncryptionStats.summary.totalErrors}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Fehler
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* PDF Encryption Logs Table */}
                                {pdfEncryptionStats.pdfEncryptionLogs && pdfEncryptionStats.pdfEncryptionLogs.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Letzte PDF-Verschlüsselungsläufe
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><strong>Zeitstempel</strong></TableCell>
                                                        <TableCell><strong>Status</strong></TableCell>
                                                        <TableCell align="right"><strong>Verschlüsselt</strong></TableCell>
                                                        <TableCell align="right"><strong>Gescannt</strong></TableCell>
                                                        <TableCell align="right"><strong>Dauer (s)</strong></TableCell>
                                                        <TableCell><strong>Details</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {pdfEncryptionStats.pdfEncryptionLogs.map((row, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                {parseTimestamp(row.timestamp)?.format('DD.MM.YYYY HH:mm:ss') || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={row.status} 
                                                                    color={row.status === 'success' ? 'primary' : 'error'}
                                                                    size="small"
                                                                    sx={row.status === 'success' ? { bgcolor: '#3caf82', color: 'white' } : { bgcolor: '#D84A48', color: 'white' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <strong>{row.recordsProcessed || 0}</strong>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {row.recordsTotal || 0}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {row.duration || 0}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" sx={{ maxWidth: 300, overflow: 'auto', display: 'block' }}>
                                                                    {(() => {
                                                                        try {
                                                                            if (!row.details) return '-';
                                                                            const parsed = JSON.parse(row.details);
                                                                            
                                                                            // Check if details has encrypted array
                                                                            if (parsed.encrypted && Array.isArray(parsed.encrypted)) {
                                                                                return `${parsed.encrypted.length} Dateien verschlüsselt`;
                                                                            }
                                                                            
                                                                            // Legacy format - just array
                                                                            return Array.isArray(parsed) ? `${parsed.length} Dateien verarbeitet` : 'Details verfügbar';
                                                                        } catch (error) {
                                                                            console.warn('Error parsing details JSON:', error);
                                                                            return 'Details verfügbar';
                                                                        }
                                                                    })()}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}


                {/* Pending Encryption Files - Always show this panel */}
                <Box sx={{ mb: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <CloudDownloadIcon sx={{ mr: 1, alignSelf: 'center' }} />
                                Ausstehende PDF-Verschlüsselung
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                PDF-Dateien im uploads/ Verzeichnis, die noch verschlüsselt werden müssen
                            </Typography>
                            
                            {pendingFiles ? (
                                <>
                                    {/* Summary */}
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                                <Typography variant="h4" sx={{ color: 'black' }}>
                                                    {pendingFiles.pdfFiles}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'black' }}>
                                                    PDF Dateien
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', borderRadius: 2, boxShadow: 2 }}>
                                                <Typography variant="h4" sx={{ color: 'black' }}>
                                                    {pendingFiles.pdfFiles}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'black' }}>
                                                    Zur Verschlüsselung
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#D84A48', position: 'relative', borderRadius: 2, boxShadow: 2 }}>
                                                <Typography variant="h4" sx={{ color: 'black' }}>
                                                    {pendingFiles.nonPdfFiles}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'black' }}>
                                                    Nicht-PDF Dateien
                                                </Typography>
                                                {pendingFiles.nonPdfFiles > 0 && (
                                                    <IconButton
                                                        onClick={handleCleanNonPdfFiles}
                                                        disabled={cleaningNonPdfFiles}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            top: 8, 
                                                            right: 8,
                                                            color: 'error.main',
                                                            bgcolor: 'white',
                                                            '&:hover': {
                                                                bgcolor: 'error.light',
                                                                color: 'white'
                                                            }
                                                        }}
                                                        size="small"
                                                    >
                                                        {cleaningNonPdfFiles ? <CircularProgress size={20} /> : <DeleteIcon />}
                                                    </IconButton>
                                                )}
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* Clean Result */}
                                    {cleanNonPdfResult && (
                                        <Alert 
                                            severity={cleanNonPdfResult.success ? 'success' : 'error'} 
                                            sx={{ mb: 3 }}
                                            onClose={() => setCleanNonPdfResult(null)}
                                        >
                                            <AlertTitle>{cleanNonPdfResult.success ? 'Erfolgreich' : 'Fehler'}</AlertTitle>
                                            {cleanNonPdfResult.message}
                                            {cleanNonPdfResult.success && cleanNonPdfResult.deletedFiles && cleanNonPdfResult.deletedFiles.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" display="block">
                                                        Gelöschte Dateien: {cleanNonPdfResult.deletedFiles.join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {cleanNonPdfResult.errors && cleanNonPdfResult.errors.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" display="block" color="error">
                                                        Fehler bei: {cleanNonPdfResult.errors.map(e => e.file).join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Alert>
                                    )}

                                    {/* Files Table */}
                                    {pendingFiles.allFiles && pendingFiles.allFiles.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                Alle Dateien im uploads/ Ordner ({pendingFiles.allFiles.length})
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Dateiname</TableCell>
                                                            <TableCell align="right">Größe</TableCell>
                                                            <TableCell>Typ/Klassifizierung</TableCell>
                                                            <TableCell>Jahr</TableCell>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell>Geändert</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pendingFiles.allFiles.map((file, index) => (
                                                            <TableRow key={index} sx={{ 
                                                                bgcolor: file.type ? 'grey.100' : 'inherit'
                                                            }}>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                        {file.name}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2">
                                                                        {(file.size / 1024).toFixed(1)} KB
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {file.classification ? (
                                                                        <Chip 
                                                                            label={file.classification} 
                                                                            size="small"
                                                                            color={
                                                                                file.classification === 'Rechnungen' ? 'primary' : 
                                                                                file.classification === 'Lieferscheine' ? 'info' : 
                                                                                file.classification === 'Gutschriften' ? 'warning' : 
                                                                                'default'
                                                                            }
                                                                            sx={
                                                                                file.classification === 'Rechnungen' ? { bgcolor: '#3caf82', color: 'white' } :
                                                                                file.classification === 'Lieferscheine' ? { bgcolor: '#4DA6FF', color: 'white' } :
                                                                                file.classification === 'Gutschriften' ? { color: 'white' } :
                                                                                {}
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <Chip 
                                                                            label={file.type || 'PDF'} 
                                                                            size="small"
                                                                            sx={{ bgcolor: '#4DA6FF', color: 'white' }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {file.year || '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {file.type ? (
                                                                        <Chip 
                                                                            label="Kein PDF" 
                                                                            size="small"
                                                                            sx={{ bgcolor: '#D84A48', color: 'white' }}
                                                                        />
                                                                    ) : (
                                                                        <Chip 
                                                                            label="Ausstehend" 
                                                                            size="small"
                                                                            color="primary"
                                                                            sx={{ bgcolor: '#3caf82', color: 'white' }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {file.modifiedAt ? parseTimestamp(file.modifiedAt)?.format('DD.MM.YYYY HH:mm') || 'Unbekannt' : 'Unbekannt'}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {(!pendingFiles.allFiles || pendingFiles.allFiles.length === 0) && (
                                        <Alert severity="success" sx={{ mt: 2 }}>
                                            <AlertTitle>✅ Alle PDFs verarbeitet!</AlertTitle>
                                            Es gibt keine ausstehenden PDF-Dateien zur Verschlüsselung. Der uploads/ Ordner ist leer.
                                        </Alert>
                                    )}
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <LinearProgress sx={{ mb: 2 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Lade Dateiinformationen...
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>


                {/* Missing Invoice Files Section */}
                {console.log('missingInvoices state:', missingInvoices)}
                <Box sx={{ mb: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        <CloudDownloadIcon sx={{ mr: 1, alignSelf: 'center' }} />
                                        Fehlende Rechnungsdateien
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Rechnungen in der Datenbank ohne verschlüsselte PDF-Datei
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={fetchMissingInvoices}
                                    disabled={loading || missingInvoicesLoading}
                                    startIcon={missingInvoicesLoading ? <LinearProgress size={16} /> : <RefreshIcon />}
                                    sx={{ borderColor: '#3caf82', color: '#3caf82', '&:hover': { borderColor: '#2d8a62', bgcolor: 'rgba(60, 175, 130, 0.04)' } }}
                                >
                                    {missingInvoicesLoading ? 'Lade fehlende Dateien...' : 'Fehlende Dateien laden'}
                                </Button>
                            </Box>

                            {missingInvoicesLoading && (
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress />
                                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                        Lade fehlende Dateien...
                                    </Typography>
                                </Box>
                            )}

                            {missingInvoices && !missingInvoicesLoading && (
                                <Box>
                                
                                {/* Summary Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#D84A48', borderRadius: 2, boxShadow: 2 }}>
                                            <Typography variant="h4" sx={{ color: 'black' }}>
                                                {missingInvoices.summary.total.toLocaleString('de-DE')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'black' }}>
                                                Fehlende Dateien
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    {Object.entries(missingInvoices.summary.byYear).map(([year, count]) => (
                                        <Grid item xs={12} sm={6} md={3} key={year}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200', borderRadius: 2, boxShadow: 2 }}>
                                                <Typography variant="h4" sx={{ color: 'black' }}>
                                                    {count.toLocaleString('de-DE')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'black' }}>
                                                    Jahr {year}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                    

                                {/* Missing Files Table */}
                                {missingInvoices.missingFiles && missingInvoices.missingFiles.length > 0 && (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    Fehlende Dateien
                                        </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(() => {
                                                        const filteredCount = missingInvoices.missingFiles.filter(invoice => 
                                                            selectedDocumentType === 'all' || 
                                                            invoice.documentType === selectedDocumentType
                                                        ).length;
                                                        return `Zeige ${filteredCount} ${selectedDocumentType === 'all' ? 'fehlende Dateien' : `${selectedDocumentType}s`}`;
                                                    })()}
                                                                </Typography>
                                            </Box>
                                            
                                            {/* Document Type Filter */}
                                            <ToggleButtonGroup
                                                value={selectedDocumentType}
                                                exclusive
                                                onChange={(event, newValue) => {
                                                    if (newValue !== null) {
                                                        setSelectedDocumentType(newValue);
                                                    }
                                                }}
                                                size="small"
                                                color="primary"
                                                sx={{ ml: 2 }}
                                            >
                                                <ToggleButton value="all">
                                                    <Chip 
                                                        label={`Alle (${missingInvoices.missingFiles.length})`} 
                                                        size="small" 
                                                        color="default" 
                                                    />
                                                </ToggleButton>
                                                <ToggleButton value="Rechnung">
                                                    <Chip 
                                                        label={`Rechnung (${missingInvoices.missingFiles.filter(f => f.documentType === 'Rechnung').length})`} 
                                                        size="small" 
                                                        color="primary" 
                                                        sx={{ bgcolor: '#3caf82', color: 'white' }}
                                                    />
                                                </ToggleButton>
                                                <ToggleButton value="Gutschrift">
                                                    <Chip 
                                                        label={`Gutschrift (${missingInvoices.missingFiles.filter(f => f.documentType === 'Gutschrift').length})`} 
                                                        size="small" 
                                                        color="warning" 
                                                    />
                                                </ToggleButton>
                                                <ToggleButton value="Lieferschein">
                                                    <Chip 
                                                        label={`Lieferschein (${missingInvoices.missingFiles.filter(f => f.documentType === 'Lieferschein').length})`} 
                                                        size="small" 
                                                        sx={{ bgcolor: '#4DA6FF', color: 'white' }}
                                                    />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                        <Box sx={{ height: 500, width: '100%', pb: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                                <Button
                                                    onClick={exportToCSV}
                                                    color="primary"
                                                    startIcon={<FileDownloadIcon />}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ borderColor: '#3caf82', color: '#3caf82', '&:hover': { borderColor: '#2d8a62', bgcolor: 'rgba(60, 175, 130, 0.04)' } }}
                                                >
                                                    CSV Export
                                                </Button>
                                            </Box>
                                            <DataGrid
                                                rows={missingInvoices.missingFiles
                                                    .filter(invoice => 
                                                        selectedDocumentType === 'all' || 
                                                        invoice.documentType === selectedDocumentType
                                                    )
                                                    .map((invoice, index) => ({
                                                        id: index,
                                                        belegnummer: invoice.invoiceNumber,
                                                        kundennummer: invoice.customerNumber,
                                                        belegart: invoice.documentType,
                                                        betrag: invoice.totalAmount || 0,
                                                        erwarteterDateiname: invoice.expectedFilename1,
                                                        // Raw data for sorting and export
                                                        rawInvoiceDate: invoice.invoiceDate,
                                                        rawTotalAmount: invoice.totalAmount
                                                    }))}
                                                sx={{
                                                    '& .MuiDataGrid-cell': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        textAlign: 'center'
                                                    },
                                                    '& .MuiDataGrid-cell[data-field="erwarteterDateiname"]': {
                                                        justifyContent: 'flex-start',
                                                        textAlign: 'left'
                                                    }
                                                }}
                                                columns={[
                                                    {
                                                        field: 'belegnummer',
                                                        headerName: 'Belegnummer',
                                                        width: 120,
                                                        align: 'center',
                                                        headerAlign: 'center',
                                                        renderCell: (params) => (
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'center', width: '100%' }}>
                                                                {params.value}
                                                                </Typography>
                                                        )
                                                    },
                                                    {
                                                        field: 'kundennummer',
                                                        headerName: 'Kundennummer',
                                                        width: 130,
                                                        align: 'center',
                                                        headerAlign: 'center'
                                                    },
                                                    {
                                                        field: 'datum',
                                                        headerName: 'Datum',
                                                        width: 110,
                                                        align: 'center',
                                                        headerAlign: 'center',
                                                        type: 'date',
                                                        valueGetter: (value, row) => {
                                                            // Use the raw date for sorting
                                                            return row.rawInvoiceDate ? new Date(row.rawInvoiceDate) : null;
                                                        },
                                                        valueFormatter: (value) => {
                                                            // Display as DD.MM.YYYY
                                                            return value ? dayjs(value).format('DD.MM.YYYY') : '-';
                                                        }
                                                    },
                                                    {
                                                        field: 'belegart',
                                                        headerName: 'Belegart',
                                                        width: 120,
                                                        align: 'center',
                                                        headerAlign: 'center',
                                                        renderCell: (params) => (
                                                                <Chip 
                                                                label={params.value} 
                                                                    size="small"
                                                                color={params.value === 'Rechnung' ? 'primary' : 
                                                                       params.value === 'Gutschrift' ? 'warning' : 'info'}
                                                                sx={
                                                                    params.value === 'Rechnung' ? { bgcolor: '#3caf82', color: 'white' } :
                                                                    params.value === 'Lieferschein' ? { bgcolor: '#4DA6FF', color: 'white' } :
                                                                    { color: 'white' }
                                                                }
                                                            />
                                                        )
                                                    },
                                                    {
                                                        field: 'betrag',
                                                        headerName: 'Betrag',
                                                        width: 120,
                                                        type: 'number',
                                                        align: 'center',
                                                        headerAlign: 'center',
                                                        renderCell: (params) => (
                                                            <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
                                                                    {new Intl.NumberFormat('de-DE', { 
                                                                        style: 'currency', 
                                                                        currency: 'EUR' 
                                                                }).format(params.value)}
                                                                </Typography>
                                                        )
                                                    },
                                                    {
                                                        field: 'erwarteterDateiname',
                                                        headerName: 'Erwarteter Dateiname',
                                                        width: 300,
                                                        align: 'left',
                                                        headerAlign: 'center',
                                                        renderCell: (params) => (
                                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                                {params.value}
                                                                </Typography>
                                                        )
                                                    }
                                                ]}
                                                pageSize={50}
                                                rowsPerPageOptions={[25, 50, 100, 250, 500]}
                                                checkboxSelection
                                                disableSelectionOnClick
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {missingInvoices.missingFiles && missingInvoices.missingFiles.length === 0 && (
                                    <Alert severity="success">
                                        <AlertTitle>Alle Rechnungen haben Dateien!</AlertTitle>
                                        Es wurden keine fehlenden Rechnungsdateien gefunden.
                                    </Alert>
                                )}
                                </Box>
                            )}

                            {!missingInvoices && (
                                <Alert severity="info">
                                    Klicken Sie auf "Fehlende Dateien laden", um die Analyse zu starten.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Table Statistics */}
                {tableStats && (
                    <Box sx={{ mb: 3 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Aktuelle Tabellen-Statistiken
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Gesamt: {tableStats.summary.totalRecords.toLocaleString('de-DE')} Datensätze in {tableStats.summary.totalTables} Tabellen
                                </Typography>
                                <Grid container spacing={2}>
                                    {tableStats.tables.map((table, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography variant="h4" color="primary">
                                                    {table.record_count.toLocaleString('de-DE')}
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {table.display_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {table.description}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}

            </Box>
        </BasicPage>
    );
};